import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { EmptyState } from "@/components/shared/EmptyState";
import { UserDetailDialog } from "@/components/shared/UserDetailDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { RequestDetailLayout } from "@/components/shared/RequestDetailLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  RequestActivityList,
  quoteRequestActivityConfig,
  type ActivityEvent,
} from "@/components/shared/RequestActivityList";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PlatformQuoteIntakeTranscript } from "@/components/features/notarial-requests/platform-quote-intake-transcript";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatUyMinor } from "@/lib/formatUyMinor";
import {
  getPlatformQuoteRequestDetail,
  overridePlatformQuoteRequestTriage,
} from "@/services/platformQuoteRequestsService";
import {
  ADMIN_STATUS_VARIANTS,
  adminStatusLabelKey,
  type AdminQuoteRequestStatus,
} from "@/utils/adminQuoteRequestView";

const QUOTE_STATUS_VARIANTS: Record<string, "info" | "success" | "softDanger"> = {
  submitted: "info",
  awarded: "success",
  rejected: "softDanger",
};

/**
 * Admin detail view for a single quote_request — same navigation convention
 * as /requests/:id (row click -> dedicated route). Deliberately proportional
 * to quote_request's actual size: no per-fulfillment-mode branching like
 * platform-notarial-request-detail-page.tsx, because quote_request doesn't
 * do fulfillment itself (that's the spawned child request's job, P5).
 *
 * Shows the request body, triage result, the discipline-assign action (same
 * as the list, for an admin who drilled in first) when still triage_failed,
 * and the full quote_request_event timeline using the backend's already
 * human-readable `message` text rather than a parallel i18n label map.
 */
/** The routing control offers one discipline, the other, or both. */
type RouteChoice = "notary" | "accountant" | "both";

export function PlatformQuoteRequestDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [discipline, setDiscipline] = useState("");
  const [requesterOpen, setRequesterOpen] = useState(false);

  /**
   * The shared quote timeline, with one admin-only change: the consumer and
   * professional views hide `message` because their label already says what
   * happened, but the backend writes the decision itself into it ("Clasificado
   * automáticamente como accountant (95% confianza)") and that IS what an
   * admin opened this page to read.
   */
  const adminQuoteActivityConfig = useMemo(
    () => ({
      ...quoteRequestActivityConfig(),
      getDetailText: (event: ActivityEvent) => event.message ?? null,
    }),
    [],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["platform", "quote-requests", "detail", id],
    queryFn: () => getPlatformQuoteRequestDetail(id as string),
    enabled: id != null,
  });

  const routeMutation = useMutation({
    mutationFn: (choice: RouteChoice) =>
      choice === "both"
        ? overridePlatformQuoteRequestTriage(id as string, "notary", "accountant")
        : overridePlatformQuoteRequestTriage(id as string, choice),
    onSuccess: () => {
      toast.success(t("platform.quoteRequestsTriage.resolveSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["platform", "quote-requests"] });
    },
    onError: () => {
      toast.error(t("platform.quoteRequestsTriage.resolveError"));
    },
  });

  if (isLoading || !data) {
    return <LoadingPage title={t("platform.quoteRequestDetail.loadingTitle")} />;
  }

  const isAwaitingManualRouting = data.status === "triage_failed";

  const activityEvents = data.events.map((e) => ({
    id: e.id,
    type: e.eventType,
    createdAt: e.createdAt,
    actorDisplayName: e.actorDisplayName,
    actorRole: e.actorRole,
    message: e.message,
  }));

  /**
   * The two bottom lists go in the layout's footer GRID rather than the
   * children stack, so they sit side by side on `lg` instead of running
   * full-bleed down an admin's wide monitor. Every other caller that uses
   * the grid pairs documents with the timeline; this page has no documents
   * card, so the quotes list takes the first slot.
   */
  const quotesCard = (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("platform.quoteRequestDetail.quotesTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.quotes.length === 0 ? (
          <EmptyState title={t("platform.quoteRequestDetail.quotesEmpty")} className="py-6" />
        ) : (
          data.quotes.map((quote) => (
            <div
              key={quote.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="text-sm font-medium text-foreground">
                  {quote.professionalDisplayName ?? quote.professionalUserId}
                  {quote.legDiscipline && data.legs.length > 1 ? (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {t(
                        quote.legDiscipline === "notary"
                          ? "platform.quoteRequestsTriage.disciplineNotary"
                          : "platform.quoteRequestsTriage.disciplineAccountant",
                      )}
                    </span>
                  ) : null}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatUyMinor(quote.priceMinor, quote.currency)}
                  {quote.estimatedDays != null
                    ? ` · ${t("platform.quoteRequestDetail.quoteEtaDays", { count: quote.estimatedDays })}`
                    : ""}
                </p>
                {quote.message && <p className="text-sm text-muted-foreground">{quote.message}</p>}
              </div>
              <StatusBadge variant={QUOTE_STATUS_VARIANTS[quote.status] ?? "info"}>
                {t(`platform.quoteRequestDetail.quoteStatus_${quote.status}`)}
              </StatusBadge>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );

  /**
   * Where the job goes. The RFQ detail was the one request surface that never
   * showed this, even though the request has carried the snapshot all along —
   * professionals see it, and a physical bid is gated on it (spec 218021de).
   *
   * Reads the snapshot columns, never the requester's current address book: a
   * consumer editing a saved address must not retroactively move a parcel that
   * was already priced and quoted.
   */
  const deliveryCard = (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("platform.quoteRequestDetail.deliveryTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.deliveryFormattedAddress?.trim() ? (
          <>
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {data.deliveryFormattedAddress}
            </p>
            {data.deliveryContactPhone || data.deliveryCourierNotes ? (
              <DetailRows className="mt-3">
                {data.deliveryContactPhone ? (
                  <DetailRow label={t("platform.quoteRequestDetail.deliveryContact")}>
                    {data.deliveryContactPhone}
                  </DetailRow>
                ) : null}
                {data.deliveryCourierNotes ? (
                  <DetailRow label={t("platform.quoteRequestDetail.deliveryNotes")}>
                    {data.deliveryCourierNotes}
                  </DetailRow>
                ) : null}
              </DetailRows>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("platform.quoteRequestDetail.deliveryNotSet")}
          </p>
        )}
      </CardContent>
    </Card>
  );

  /**
   * What professionals have DONE to this request, beyond bidding.
   *
   * Clarifications have no UI anywhere else in the product, and blocked ones
   * have no audience at all — the service saves them and every read path
   * filters them out — so this card is the only place either is visible
   * (spec b3430a37).
   *
   * The ordinal beside the name is load-bearing, not decoration: the quotes
   * list and the timeline label professionals "Profesional N", and this is
   * what lets an admin work out which human that is.
   */
  const activityCard = (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("platform.quoteRequestDetail.activityTitle")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("platform.quoteRequestDetail.activityHint")}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.clarifications.length === 0 ? (
          <EmptyState title={t("platform.quoteRequestDetail.activityEmpty")} className="py-6" />
        ) : (
          data.clarifications.map((c) => (
            <div key={c.id} className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-foreground">
                  {c.askedByName ?? t("platform.quoteRequestDetail.activityUnknownPro")}
                </p>
                {c.askedByOrdinal != null ? (
                  <span className="text-xs text-muted-foreground">
                    {t("platform.quoteRequestDetail.activityOrdinal", { n: c.askedByOrdinal })}
                  </span>
                ) : null}
                {c.legDiscipline ? (
                  <span className="text-xs text-muted-foreground">
                    {t(
                      c.legDiscipline === "notary"
                        ? "platform.quoteRequestsTriage.disciplineNotary"
                        : "platform.quoteRequestsTriage.disciplineAccountant",
                    )}
                  </span>
                ) : null}
                {/* Blocked FIRST and as a badge: an admin scanning this card
                    needs to see "never delivered" before reading the text. */}
                {c.blockedReason ? (
                  <StatusBadge variant="softDanger">
                    {t(`platform.quoteRequestDetail.activityBlocked_${c.blockedReason}`)}
                  </StatusBadge>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDisplayDate(c.createdAt)}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{c.question}</p>
              {/* A blocked question has no answer and never will — saying
                  "awaiting answer" there would send an admin to chase the
                  consumer about a message they were never shown. */}
              {c.blockedReason ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("platform.quoteRequestDetail.activityBlockedHint")}
                </p>
              ) : c.answer ? (
                <p className="mt-2 whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-sm text-muted-foreground">
                  {c.answer}
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("platform.quoteRequestDetail.activityUnanswered")}
                </p>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );

  const timelineCard = (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("platform.quoteRequestDetail.timelineTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <RequestActivityList
          events={activityEvents}
          config={adminQuoteActivityConfig}
          emptyMessage={t("platform.quoteRequestDetail.timelineEmpty")}
        />
      </CardContent>
    </Card>
  );

  return (
    <RequestDetailLayout
      title={data.title}
      description={data.description}
      onBack={() => navigate("/platform/quote-requests")}
      primary={quotesCard}
      secondary={timelineCard}
      overlays={
        <UserDetailDialog
          userId={data.requestedByUserId}
          open={requesterOpen}
          onOpenChange={setRequesterOpen}
        />
      }
    >

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("platform.quoteRequestDetail.summaryTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRows>
            <DetailRow label={t("platform.quoteRequestsTriage.colRequest")}>
              <StatusBadge
                variant={
                  ADMIN_STATUS_VARIANTS[data.status as keyof typeof ADMIN_STATUS_VARIANTS] ??
                  "info"
                }
              >
                {t(adminStatusLabelKey(data.status))}
              </StatusBadge>
            </DetailRow>
            <DetailRow label={t("platform.quoteRequestsTriage.colDiscipline")}>
              {data.discipline !== "undecided"
                ? t(`platform.quoteRequestsTriage.discipline${data.discipline === "notary" ? "Notary" : "Accountant"}`)
                : "—"}
            </DetailRow>
            <DetailRow label={t("platform.quoteRequestDetail.requester")}>
              {/* A link, not text: an admin deciding anything about this
                  request routinely needs to know who asked — their contact
                  details and where they receive things — and there is no other
                  admin surface for a consumer (spec 218021de). */}
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setRequesterOpen(true)}
              >
                {data.requesterName ?? data.requesterEmail ?? "—"}
              </Button>
            </DetailRow>
            <DetailRow label={t("platform.quoteRequestDetail.createdAt")}>
              {formatDisplayDate(data.createdAt)}
            </DetailRow>
          </DetailRows>

          {data.triageResult && (
            <div className="mt-4 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
              {data.triageResult.rationale}
              <br />
              {t("platform.quoteRequestsTriage.confidenceLabel", {
                value: Math.round(data.triageResult.confidence * 100),
              })}
            </div>
          )}

          {isAwaitingManualRouting && (
            <div className="mt-4 flex items-center gap-2">
              <Select value={discipline} onValueChange={setDiscipline}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t("platform.quoteRequestsTriage.disciplinePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notary">{t("platform.quoteRequestsTriage.disciplineNotary")}</SelectItem>
                  <SelectItem value="accountant">{t("platform.quoteRequestsTriage.disciplineAccountant")}</SelectItem>
                  {/* Opens two legs — two professionals, quoted and paid
                      separately. Last, because it is the costlier choice for
                      the consumer and should not be picked by reflex. */}
                  <SelectItem value="both">{t("platform.quoteRequestsTriage.disciplineBoth")}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={discipline === "" || routeMutation.isPending}
                onClick={() => routeMutation.mutate(discipline as RouteChoice)}
              >
                {t("platform.quoteRequestsTriage.confirmButton")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {data.legs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {t("platform.quoteRequestDetail.legsTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.legs.map((leg) => (
              <div
                key={leg.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
              >
                <span className="font-medium text-foreground">
                  {t(
                    leg.discipline === "notary"
                      ? "platform.quoteRequestsTriage.disciplineNotary"
                      : "platform.quoteRequestsTriage.disciplineAccountant",
                  )}
                </span>
                {/*
                  The leg status used to render as a raw lowercase enum
                  (`quoting`) beside a translated discipline. Reuses the
                  list's label map; the ?? "info" arm is load-bearing, since
                  leg-only values like `paid` and `fulfillment_failed` fall
                  outside the quote_request vocabulary (spec 2d40b7e, C-3).
                */}
                <StatusBadge
                  variant={ADMIN_STATUS_VARIANTS[leg.status as AdminQuoteRequestStatus] ?? "info"}
                >
                  {t(adminStatusLabelKey(leg.status))}
                </StatusBadge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {deliveryCard}

      {activityCard}

      {/* Placed directly above the quotes, and below the routing control it
          exists to inform — spec add326e0 §4d.2. */}
      {data.intake && <PlatformQuoteIntakeTranscript intake={data.intake} />}

      {data.childRequestId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("platform.quoteRequestDetail.childLinkTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {t("platform.quoteRequestDetail.childLinkDescription")}
            </p>
            <Button onClick={() => navigate(`/requests/${data.childRequestId}`)}>
              {t("platform.quoteRequestDetail.childLinkButton")}
            </Button>
          </CardContent>
        </Card>
      )}

    </RequestDetailLayout>
  );
}
